<template>
    <div ref="element" class="answer_question">
        <template v-for="(item,index) in question.detail">
            <div v-if="!item.hasChildren"
                 :key="index"
                 :class="{'split_line': item.questionBackground.id === 2, 'split_block': item.questionBackground.id === 3}"
                 class="answer">
                <div class="set_piece left" :value="3"></div>
                <div class="set_piece right" :value="3"></div>
                <div class="editor">
                    <div class="score_head">
                        <div v-for="(block, key) in showScores(item.questionValue)" :key="key">{{block}}</div>
                    </div>
                    <ckeditor :editor="editor" v-model="item.html" :config="editorConfig"></ckeditor>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
    import CKEditor from '@ckeditor/ckeditor5-vue';
    import InlineEditor from '@ckeditor/ckeditor5-editor-inline/src/inlineeditor';

    import EssentialsPlugin from '@ckeditor/ckeditor5-essentials/src/essentials';
    import BoldPlugin from '@ckeditor/ckeditor5-basic-styles/src/bold';
    import ItalicPlugin from '@ckeditor/ckeditor5-basic-styles/src/italic';
    import LinkPlugin from '@ckeditor/ckeditor5-link/src/link';
    import Heading from '@ckeditor/ckeditor5-heading/src/heading'
    import ParagraphPlugin from '@ckeditor/ckeditor5-paragraph/src/paragraph';
    import SimpleUploadAdapter  from '@ckeditor/ckeditor5-upload/src/adapters/simpleuploadadapter';
    import Image from '@ckeditor/ckeditor5-image/src/image';
    import ImageToolbar from '@ckeditor/ckeditor5-image/src/imagetoolbar';
    // import ImageCaption from '@ckeditor/ckeditor5-image/src/imagecaption';
    import ImageStyle from '@ckeditor/ckeditor5-image/src/imagestyle';
    import ImageResize from '@ckeditor/ckeditor5-image/src/imageresize';
    import ImageUpload from '@ckeditor/ckeditor5-image/src/imageupload';
    import LinkImage from '@ckeditor/ckeditor5-link/src/linkimage';
    import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
    import ButtonView from '@ckeditor/ckeditor5-ui/src/button/buttonview';

    import imageIcon from '@ckeditor/ckeditor5-core/theme/icons/pencil.svg';
    import InsertLineIcon from '@ckeditor/ckeditor5-core/theme/icons/align-middle.svg';
    class InsertBlank extends Plugin {
        init() {
            const editor = this.editor;

            editor.ui.componentFactory.add( 'insertBlank', locale => {
                const view = new ButtonView( locale );

                view.set( {
                    label: 'Insert Blank',
                    icon: imageIcon,
                    tooltip: true
                } );

                // Callback executed once the image is clicked.
                view.on( 'execute', () => {
                    const docFrag = editor.model.change( writer => {
                        const p1 = writer.createElement( 'paragraph' );
                        const p2 = writer.createElement( 'heading1');
                        // const p2 = writer.createAttributeElement( 'p',{ class: 'center' } );
                        const blockQuote = writer.createElement( 'blockQuote' );
                        const docFrag = writer.createDocumentFragment();
                        writer.append( p1, docFrag );
                        writer.append( blockQuote, docFrag );
                        writer.append( p2, blockQuote );
                        writer.insertText( '', p1 );
                        writer.insertText( '', p2 );

                        return docFrag;
                    } );

// insertContent() does not have to be used in a change() block. It can, though,
// so this code could be moved to the callback defined above.
                    editor.model.insertContent( docFrag );
                } );

                return view;
            } );
        }
    }
    class InsertUnderline extends Plugin {
        init() {
            const editor = this.editor;

            editor.ui.componentFactory.add( 'insertUnderline', locale => {
                const view = new ButtonView( locale );

                view.set( {
                    label: 'Insert Line',
                    icon: InsertLineIcon,
                    tooltip: true
                } );

                // Callback executed once the image is clicked.
                view.on( 'execute', () => {
                    editor.model.change( writer => {
                        writer.insertText( '__________________________________', editor.model.document.selection.getFirstPosition() );
                    } );
                } );

                return view;
            } );
        }
    }
    function ConvertDivAttributes( editor ) {
        // Allow <div> elements in the model.
        editor.model.schema.register( 'div', {
            allowWhere: '$block',
            allowContentOf: '$root'
        } );

        // Allow <div> elements in the model to have all attributes.
        editor.model.schema.addAttributeCheck( context => {
            if ( context.endsWith( 'div' ) ) {
                return true;
            }
        } );

        // View-to-model converter converting a view <div> with all its attributes to the model.
        editor.conversion.for( 'upcast' ).elementToElement( {
            view: 'div',
            model: ( viewElement, modelWriter ) => {
                return modelWriter.createElement( 'div', viewElement.getAttributes() );
            }
        } );

        // Model-to-view converter for the <div> element (attributes are converted separately).
        editor.conversion.for( 'downcast' ).elementToElement( {
            model: 'div',
            view: 'div'
        } );

        // Model-to-view converter for <div> attributes.
        // Note that a lower-level, event-based API is used here.
        editor.conversion.for( 'downcast' ).add( dispatcher => {
            dispatcher.on( 'attribute', ( evt, data, conversionApi ) => {
                // Convert <div> attributes only.
                if ( data.item.name !== 'div' ) {
                    return;
                }

                const viewWriter = conversionApi.writer;
                const viewDiv = conversionApi.mapper.toViewElement( data.item );

                // In the model-to-view conversion we convert changes.
                // An attribute can be added or removed or changed.
                // The below code handles all 3 cases.
                if ( data.attributeNewValue ) {
                    viewWriter.setAttribute( data.attributeKey, data.attributeNewValue, viewDiv );
                } else {
                    viewWriter.removeAttribute( data.attributeKey, viewDiv );
                }
            } );
        } );
    }
    function AddClassToAllHeading1( editor ) {
        // Both the data and the editing pipelines are affected by this conversion.
        editor.conversion.for( 'downcast' ).add( dispatcher => {
            // Headings are represented in the model as a "heading1" element.
            // Use the "low" listener priority to apply the changes after the headings feature.
            dispatcher.on( 'insert:heading1', ( evt, data, conversionApi ) => {
                const viewWriter = conversionApi.writer;
                viewWriter.addClass( 'heading_blank', conversionApi.mapper.toViewElement( data.item ) );
            }, { priority: 'low' } );
        } );
    }
    class CustomFigureAttributes {
        /**
         * Plugin's constructor - receives editor instance on creation.
         */
        constructor( editor ) {
            // Save reference to the editor.
            this.editor = editor;
        }

        /**
         * Setups conversion and extends table & image features schema.
         *
         * Schema extending must be done in the “afterInit()” call because plugins define their schema in “init()“.
         */
        afterInit() {
            const editor = this.editor;


            // editor.conversion.for( 'upcast' ).add( upcastCustomClasses( 'figure' ), { priority: 'low' } );

            // Define custom attributes that should be preserved.
            setupCustomAttributeConversion( 'img', 'image', 'crossOrigin', editor );
            // setupCustomAttributeConversion( 'table', 'table', 'id', editor );
        }
    }
    /**
     * Sets up a conversion for a custom attribute on view elements contained inside a <figure>.
     *
     * This method:
     * - Adds proper schema rules.
     * - Adds an upcast converter.
     * - Adds a downcast converter.
     */
    function setupCustomAttributeConversion( viewElementName, modelElementName, viewAttribute, editor ) {
        // Extend the schema to store an attribute in the model.
        const modelAttribute = `custom${ viewAttribute }`;
        editor.model.schema.extend( modelElementName, { allowAttributes: [ modelAttribute ] } );

        editor.conversion.for( 'upcast' ).add( upcastAttribute( viewElementName, viewAttribute, modelAttribute ) );
        editor.conversion.for( 'downcast' ).add( downcastAttribute( modelElementName, viewElementName, viewAttribute, modelAttribute ) );
    }
    function findViewChild( viewElement, viewElementName, conversionApi ) {
        const viewChildren = Array.from( conversionApi.writer.createRangeIn( viewElement ).getItems() );

        return viewChildren.find( item => item.is( viewElementName ) );
    }
    /**
     * Returns the custom attribute upcast converter.
     */
    function upcastAttribute( viewElementName, viewAttribute, modelAttribute ) {
        return dispatcher => dispatcher.on( `element:${ viewElementName }`, ( evt, data, conversionApi ) => {
            const viewItem = data.viewItem;
            const modelRange = data.modelRange;

            const modelElement = modelRange && modelRange.start.nodeAfter;

            if ( !modelElement ) {
                return;
            }
            let value = viewItem.getAttribute( viewAttribute ) || 'anonymous';
            conversionApi.writer.setAttribute( modelAttribute, value, modelElement );
        } );
    }

    /**
     * Returns the custom attribute downcast converter.
     */
    function downcastAttribute( modelElementName, viewElementName, viewAttribute, modelAttribute ) {
        return dispatcher => dispatcher.on( `insert:${ modelElementName }`, ( evt, data, conversionApi ) => {
            const modelElement = data.item;

            const viewFigure = conversionApi.mapper.toViewElement( modelElement );
            const viewElement = findViewChild( viewFigure, viewElementName, conversionApi );

            if ( !viewElement ) {
                return;
            }
            let value = modelElement.getAttribute( modelAttribute ) || 'anonymous';
            conversionApi.writer.setAttribute( viewAttribute, value , viewElement );
            // conversionApi.writer.setAttribute( viewAttribute, 'anonymous', viewElement );
        } );
    }
    export default {
        name: "AnswerQuestion",
        data () {
            return {
                timeout: null,
                scores1: ['对','错','',' ','5','4','3','2','1','','9','8','7','6','5','4','3','2', '1', '0','', '0.5'],
                scores2: ['对','错','',' ','16','15','14','13','12','11','10','9','8','7','6','5','4','3','2', '1',' ', '0.5'],
                editor: InlineEditor,
                editorData: '<p>Content of the editor.</p>',
                editorConfig: {
                    plugins: [
                        EssentialsPlugin,
                        Heading,
                        InsertBlank,
                        InsertUnderline,
                        BoldPlugin,
                        ItalicPlugin,
                        LinkPlugin,
                        ParagraphPlugin,
                        Image,
                        ImageToolbar,
                        // ImageCaption,
                        ImageStyle,
                        ImageResize,
                        ImageUpload,
                        LinkImage,
                        SimpleUploadAdapter
                    ],
                    extraPlugins: [ConvertDivAttributes, AddClassToAllHeading1, CustomFigureAttributes],
                    toolbar: {
                        items: [
                            'bold',
                            'imageUpload',
                            'insertBlank',
                            'italic',
                            'insertUnderline',
                            // 'fontSize',
                            'link',
                            'undo',
                            'redo'
                        ]
                    },
                    image: {
                        // You need to configure the image toolbar, too, so it uses the new style buttons.
                        toolbar: [ 'imageTextAlternative', 'imageStyle:alignLeft', 'imageStyle:full', 'imageStyle:alignRight' ],

                        styles: [
                            // This option is equal to a situation where no style is applied.
                            'full',

                            // This represents an image aligned to the left.
                            'alignLeft',

                            // This represents an image aligned to the right.
                            'alignRight'
                        ]
                    },
                    simpleUpload: {
                        // uploadUrl: `${this.$APIURL}/api/tk/question/uploadQuestionImg?userToken=${this.$userInfo.userToken}`
                        uploadUrl: `${this.$APIURL}/api/tk/question/uploadQuestionImgPublic?userToken=${this.$userInfo.userToken}`
                    }
                }
            }
        },
        props: ['question'],
        components: {
            ckeditor: CKEditor.component
        },
        computed: {
            orderChange () {
                let arr = this.question.detail.map(item => item.questionShowOrder);
                return arr.join('')
            },
        },
        watch: {
            orderChange () {
                this.question.detail.forEach(item => {
                    let htmlList = item.html.split(String(item.questionOriginOrder));
                    let right = htmlList.slice(1,).join(String(item.questionOriginOrder));
                    item.html = `${htmlList[0]}${item.questionShowOrder}${right}`;
                    item.questionOriginOrder = item.questionShowOrder;
                })
            },
            editorData (newV) {
                console.log(newV)
            },
            question: {
                deep: true,
                handler () {
                    this.$nextTick(() => {
                        this.debounce()
                    })
                }
            }
        },
        created () {
            if (this.timeout) {
                clearTimeout(this.timeout);
                this.timeout = null;
            }
        },
        mounted () {
            this.$nextTick(() => {
                this.setHeight()
            })
        },
        beforeDestroy () {
            if (this.timeout) {
                clearTimeout(this.timeout);
                this.timeout = null;
            }
        },
        methods: {
            showScores (value) {
                if (value > 16) {
                    let arr = Array.from(new Array(Math.ceil(value % 10 !== 0 ? value / 10 : value / 10 + 1)).keys());
                    arr.shift();
                    arr.reverse();
                    arr = arr.concat(['','9','8','7','6','5','4','3','2', '1', '0','', '0.5']);
                    let len = 20 - arr.length;
                    let empty = [];
                    for (let i=0; i < len; i++) {
                        empty.push('')
                    }
                    return ['对','错'].concat(empty, arr);
                }
                let arr = Array.from(new Array(Number(value) + 1).keys());
                arr.shift();
                arr.reverse();
                arr = arr.concat(['0', '', '0.5']);
                let len = 20 - arr.length;
                let empty = [];
                for (let i=0; i < len; i++) {
                    empty.push('')
                }
                return ['对','错'].concat(empty, arr);
            },
            // 防抖
            debounce() {
                if(this.timeout !== null)   clearTimeout(this.timeout);
                this.timeout = null;
                this.timeout = setTimeout(() => {
                    this.setHeight()
                }, 200)
            },
            setHeight () {
                this.question.height = this.$refs.element.offsetHeight;
                this.$nextTick(() => {
                    let list = document.querySelectorAll('.split_block');
                    list.forEach(item => {
                        let children = item.lastChild.lastChild.childNodes;
                        let index = 0;
                        children.forEach((p) => {
                            if (p.nodeName === 'P') {
                                if ((index + 1) % 5 === 0) {
                                    p.setAttribute('data-index', `${(index + 1)/ 5}00`)
                                } else {
                                    p.setAttribute('data-index', '')
                                }
                                index++
                            }
                        })
                    });
                })
            },
            dd () {
                console.log(1)
            }
        }
    }
</script>

<style lang="scss" scoped>
    .answer_question{
        padding-top: 5px;
        .answer{
            font-size: 14px;
            color: #333333;
            padding: 0 40px;
            line-height: 56px;
            position: relative;
            .set_piece{
                position: absolute;
                width: 20px;
                height: 12px;
                background: rgba(0,0,0,1);
                top: 14px;
                &.left{
                    left: 0;
                }
                &.right{
                    right: 0;
                }
            }
            .editor{
                border-left: 1px solid #000000;
                border-right: 1px solid #000000;
                border-top: 0 solid transparent;
                border-bottom: 1px solid #000000;
                .score_head{
                    height: 40px;
                    line-height: 40px;
                    border-bottom: 1px solid #000000;
                    display: flex;
                    align-items: center;
                    justify-content: flex-start;
                    div{
                        width: 44px;
                        height: 40px;
                        text-align: center;
                        border-left: 1px solid #000000;
                        &:first-child{
                            border-left: 1px solid transparent;
                        }
                    }
                }
            }
            &:first-child{
                .editor{
                    border-top: 1px solid #000000;
                }
            }
            /deep/ .ck.ck-editor__editable_inline{
                padding: 20px 10px 10px 10px;
                min-height: 100px;
                p{
                    margin: 0;
                    white-space: pre-wrap;
                    word-break: break-all;
                }
                .heading_blank{
                    margin: 0;
                    font-weight: normal;
                    font-size: 14px;
                }
            }
            &.split_line{
                /deep/ .ck.ck-editor__editable_inline{
                    p{
                        position: relative;
                        &:before{
                            content: '';
                            position: absolute;
                            left: 0;
                            bottom: 21px;
                            width: calc(100% - 4px);
                            height: 1px;
                            background: #333;
                        }
                    }
                }
            }
            &.split_block{
                /deep/ .ck.ck-editor__editable_inline{
                    p{
                        position: relative;
                        &:after{
                            content: attr(data-index);
                            position: absolute;
                            text-indent: 886px;
                            width: 921px;
                            height: 46px;
                            line-height: 99px;
                            text-align: left;
                            left: calc(50% - 447px);
                            top: calc(50% - 20px);
                            background-size: 46px 46px;
                            background-image: linear-gradient(to right, transparent 47.5px, #333 49px),
                            linear-gradient(to left, transparent 44.5px, #333 46px),
                            linear-gradient(to top, transparent 44.5px, #333 46px),
                            linear-gradient(to bottom, transparent 44.5px, #333 46px);
                        }
                        &:last-child{
                            margin-bottom: 10px;
                        }
                    }
                }
            }
        }
    }
</style>
